import { FC, useCallback, useEffect, useState } from 'react';
import {
  Button,
  MenuItem,
  Select,
  InputBase,
  IconButton,
  debounce,
  CircularProgress,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { CalendlyAvailabilityZone, CalendlyEventType, CalendlyPreference } from '@demind-inc/core';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';

import './CalendlySettings.scss';
import { ReactComponent as CalendlyLogo } from '../../../../assets/icons/calendly_color.svg';
import {
  getCalendlyAuthUrl,
  useAuthContext,
  useCalendlyEventTypes,
  useCalendlyIntegInfo,
  useUpdateCalendlyPref,
} from '../../../../data-access';

const energyZones: Array<{ key: CalendlyAvailabilityZone; labelKey: string }> = [
  { key: 'high_energy', labelKey: 'settings.calendly.highEnergy' },
  { key: 'medium_energy', labelKey: 'settings.calendly.standardEnergy' },
  { key: 'low_energy', labelKey: 'settings.calendly.lowEnergy' },
];

const CalendlySettings: FC = () => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const { updateCalendlyPref } = useUpdateCalendlyPref();
  const { calendlyIntegInfo, isLoading: isLoadingCalendlyIntegInfo } = useCalendlyIntegInfo({
    calendlyIntegId: user.calendlyIntegrationId,
  });
  const { calendlyEventTypes, isLoading: isLoadingEventTypes } = useCalendlyEventTypes({
    calendlyIntegId: user.calendlyIntegrationId,
  });

  const [zones, setZones] = useState<CalendlyAvailabilityZone[]>([]);
  const [selectedEventTypeUri, setSelectedEventTypeUri] = useState<string>();

  const isCalendlyConnected = !!user.calendlyIntegrationId && !isEmpty(calendlyIntegInfo);

  useEffect(() => {
    if (!calendlyIntegInfo.preference) {
      return;
    }
    if (calendlyIntegInfo.preference.showAvailabilityZoneOf) {
      setZones(calendlyIntegInfo.preference.showAvailabilityZoneOf);
    }
    if (calendlyIntegInfo.preference.baseEventType) {
      setSelectedEventTypeUri(calendlyIntegInfo.preference.baseEventType.url);
    }
    return;
  }, [calendlyIntegInfo]);

  const handleConnectCalendly = async () => {
    const authUrl = await getCalendlyAuthUrl();
    window.location.href = authUrl;
  };

  const debouncedUpdateCalendlySettings = useCallback(
    debounce((newData: Partial<CalendlyPreference>) => {
      if (user.calendlyIntegrationId) {
        updateCalendlyPref({
          calendlyIntegrationId: user.calendlyIntegrationId,
          preferences: newData,
        });
      }
    }, 2000),
    [user.calendlyIntegrationId]
  );

  const handleUpdateZones = (newZones: CalendlyAvailabilityZone[]) => {
    setZones(newZones);
    debouncedUpdateCalendlySettings({
      showAvailabilityZoneOf: newZones,
    });
  };

  const handleEventTypeSelect = (newEventTypeUrl: string) => {
    setSelectedEventTypeUri(newEventTypeUrl);

    const targetEventType = calendlyEventTypes.find(
      (eventType) => eventType.uri === newEventTypeUrl
    );

    debouncedUpdateCalendlySettings({
      baseEventType: {
        url: newEventTypeUrl,
        rawJson: JSON.stringify(targetEventType),
      },
    });
  };

  return (
    <div className="calendly-settings">
      <div className="calendly-settings__title">{t('settings.calendly.calendlyConnect')}</div>
      {isLoadingCalendlyIntegInfo && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress size={16} />
        </div>
      )}
      {!isLoadingCalendlyIntegInfo && (
        <>
          <div className="calendly-settings__section">
            <div className="calendly-settings__section__content">
              <div className="calendly-settings__section__content__left">
                <CalendlyLogo className="calendly-settings__logo" />
                <div className="calendly-settings__section__content__left__text">
                  {t('settings.calendly.connectAccount')}
                </div>
              </div>
              <Button
                variant="contained"
                className="calendly-settings__connect-btn"
                disabled={isCalendlyConnected}
                onClick={handleConnectCalendly}
              >
                {isCalendlyConnected
                  ? t('settings.common.connected')
                  : t('settings.common.connect')}
              </Button>
            </div>
          </div>
          {isCalendlyConnected && (
            <>
              <div className="calendly-settings__section">
                <div className="calendly-settings__section__title">
                  {t('settings.common.preferences')}
                </div>
                <div className="calendly-settings__section__content">
                  <div className="calendly-settings__section__content__left__text">
                    {t('settings.calendly.showAvailabilityDuring')}
                  </div>
                  <Select
                    multiple
                    value={zones}
                    onChange={(e) =>
                      handleUpdateZones(
                        typeof e.target.value === 'string'
                          ? e.target.value.split(',').map((z) => z as CalendlyAvailabilityZone)
                          : e.target.value
                      )
                    }
                    MenuProps={{
                      PaperProps: {
                        className: 'calendly-settings__menu-paper',
                      },
                    }}
                    className="calendly-settings__select"
                    input={<InputBase />}
                  >
                    {energyZones.map((z) => (
                      <MenuItem
                        value={z.key}
                        key={z.key}
                        className="calendly-settings__select__item"
                      >
                        {t(z.labelKey)}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
                <div className="calendly-settings__section__content">
                  <div className="calendly-settings__section__content__left__text">
                    {t('settings.calendly.baseScheduleLink')}
                  </div>
                  <Select
                    value={selectedEventTypeUri || 'default'}
                    onChange={(e) => handleEventTypeSelect(e.target.value)}
                    className="calendly-settings__select"
                    input={<InputBase />}
                    MenuProps={{
                      PaperProps: {
                        className: 'calendly-settings__menu-paper',
                      },
                    }}
                    displayEmpty
                    disabled={isLoadingEventTypes}
                  >
                    <MenuItem value="default" disabled className="calendly-settings__select__item">
                      {isLoadingEventTypes ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <CircularProgress size={16} />
                          {t('settings.common.loading')}
                        </div>
                      ) : (
                        t('settings.calendly.selectEventType')
                      )}
                    </MenuItem>
                    {calendlyEventTypes.map((eventType) => (
                      <MenuItem
                        key={eventType.uri}
                        value={eventType.uri}
                        className="calendly-settings__select__item"
                      >
                        {eventType.name}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              </div>
              <div className="calendly-settings__section">
                <div className="calendly-settings__link">
                  {calendlyIntegInfo.scheduleLink}
                  <IconButton
                    className="calendly-settings__link__copy-btn"
                    onClick={() => navigator.clipboard.writeText(calendlyIntegInfo.scheduleLink)}
                    size="small"
                  >
                    <ContentCopyIcon style={{ fontSize: '14px' }} />
                  </IconButton>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default CalendlySettings;
